import { useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigationGuard } from "@/lib/navigation-guard";
import { reconcileEndpointAgentStatus, type EndpointAgentStatus } from "@/lib/live-agent-status";
import {
  LayoutDashboard,
  Phone,
  Users,
  Settings,
  ListOrdered,
  Menu,
  Shield,
  ShieldAlert,
  ShieldBan,
  Tag,
  UserCog,
  BookOpen,
  ChevronDown,
  Clock,
  Voicemail,
  Music,
  Hash,
  Sliders,
  UserCheck,
  MessageSquare,
  DollarSign,
  CalendarDays,
  UserCircle,
  BookUser,
  Ticket,
  History,
  BarChart3,
  CheckSquare,
  Activity,
  PhoneOutgoing,
  Webhook,
  Key,
  Headphones,
  FileText,
  Send,
  Eye,
  ScrollText,
  Monitor,
  ArrowRightLeft,
  FlaskConical,
  Plug,
  Workflow,
  FileBarChart,
  Library,
  Bell,
  MessagesSquare,
  ExternalLink,
  Bot,
  ClipboardList,
  Star,
  SmilePlus,
  MessageCircle,
  Camera,
  ShieldCheck,
  GitBranch,
  BrainCircuit,
  Mail,
  AtSign,
  Palette,
  Sparkles,
} from "lucide-react";
import logoPath from "@assets/logo.png";
import { usePlatformBranding } from "@/hooks/use-platform-branding";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { usePlivo } from "@/lib/plivo-context";
import { useAuth } from "@/lib/auth-context";
import { usePermissions } from "@/lib/permissions-context";
import { apiRequest, queryClient } from "@/lib/queryClient";

const SYSTEM_STATUSES = ["ready", "not_ready", "on_call", "after_call_work", "offline", "ringing"];

const statusOptions = [
  { value: "ready", label: "Ready", color: "bg-green-500", selectable: true },
  { value: "not_ready", label: "Not Ready", color: "bg-yellow-500", selectable: true },
  { value: "on_call", label: "On Call", color: "bg-blue-500", selectable: false },
  { value: "ringing", label: "Ringing", color: "bg-amber-500", selectable: false },
  { value: "after_call_work", label: "After Call Work", color: "bg-orange-500", selectable: true },
  { value: "offline", label: "Offline", color: "bg-muted-foreground", selectable: true },
];

export function AppSidebar() {
  const [location] = useLocation();
  const { isRegistered, connectedEndpointId, disconnect, callState } = usePlivo();
  const { user, logout, isImpersonating, impersonatingTenantName } = useAuth();
  const { hasPermission } = usePermissions();
  const { guardActive, requestNavigation } = useNavigationGuard();

  const handleSidebarClick = useCallback((e: React.MouseEvent) => {
    if (!guardActive) return;
    const anchor = (e.target as HTMLElement).closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;
    e.preventDefault();
    e.stopPropagation();
    requestNavigation(href);
  }, [guardActive, requestNavigation]);

  const { data: agentStatus } = useQuery({
    queryKey: ["/api/agent-statuses", connectedEndpointId],
    enabled: !!connectedEndpointId,
    staleTime: 0,
    structuralSharing: (previous, fetched) => reconcileEndpointAgentStatus(
      previous as EndpointAgentStatus | undefined, fetched as EndpointAgentStatus,
    ),
  });

  const { data: customStatuses } = useQuery<Array<{ id: string; name: string; color: string; isActive: boolean }>>({
    queryKey: ["/api/custom-agent-statuses"],
  });

  const allStatusOptions = useMemo(() => {
    const customActive = (customStatuses || []).filter(s => s.isActive).map(s => ({
      value: s.name,
      label: s.name,
      color: s.color,
      selectable: true,
    }));
    return [...statusOptions, ...customActive];
  }, [customStatuses]);

  const statusMutation = useMutation({
    mutationFn: async (selectedValue: string) => {
      if (!connectedEndpointId) return;
      sessionStorage.removeItem("conv_tab_prev_status");
      sessionStorage.removeItem("conv_tab_prev_label");
      const isSystem = SYSTEM_STATUSES.includes(selectedValue);
      await apiRequest("PUT", `/api/agent-statuses/${connectedEndpointId}`, {
        status: isSystem ? selectedValue : "not_ready",
        customLabel: isSystem ? null : selectedValue,
      });
    },
    onSuccess: (_, selectedValue) => {
      // Immediately paint the new status in the sidebar pill without waiting for
      // the background refetch that `invalidateQueries` triggers.  The refetch
      // still runs for server-confirmed accuracy, but the user sees the change
      // the instant they click.
      if (connectedEndpointId) {
        const isSystem = SYSTEM_STATUSES.includes(selectedValue);
        const newStatus = isSystem ? selectedValue : "not_ready";
        const newLabel  = isSystem ? null : selectedValue;
        queryClient.setQueryData<any>(
          ["/api/agent-statuses", connectedEndpointId],
          (old: any) => old ? { ...old, status: newStatus, customLabel: newLabel } : old,
        );
      }
      queryClient.invalidateQueries({ queryKey: ["/api/agent-statuses"] });
      try {
        const ch = new BroadcastChannel("callkarma_call_sync");
        ch.postMessage({ type: "status_changed" });
        ch.close();
      } catch (_e) {}
    },
  });

  const forceReadyMutation = useMutation({
    mutationFn: async () => {
      if (!connectedEndpointId) return;
      await apiRequest("POST", `/api/agent-statuses/${connectedEndpointId}/force-ready`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent-statuses"] });
      try {
        const ch = new BroadcastChannel("callkarma_call_sync");
        ch.postMessage({ type: "status_changed" });
        ch.close();
      } catch (_e) {}
    },
  });

  const rawStatus = (agentStatus as any)?.status || "offline";
  const customLabel = (agentStatus as any)?.customLabel;
  const currentStatus = customLabel && rawStatus === "not_ready" ? customLabel : rawStatus;
  const isStuckOnCall = rawStatus === "on_call" && callState === "idle";
  const currentStatusInfo = allStatusOptions.find(s => s.value === currentStatus) || statusOptions[4];

  const effectiveRole = isImpersonating ? "tenant_admin" : (user?.role || "agent");

  const isSuperAdminView = effectiveRole === "super_admin";

  const { data: ticketingStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/ticketing/enabled"],
    enabled: !isSuperAdminView,
  });
  const ticketingEnabled = ticketingStatus?.enabled === true;

  const { data: powerDialerStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/power-dialer/enabled"],
    enabled: !isSuperAdminView,
  });
  const powerDialerEnabled = powerDialerStatus?.enabled === true;

  const { data: previewDialerStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/preview-dialer/enabled"],
    enabled: !isSuperAdminView,
  });
  const previewDialerEnabled = previewDialerStatus?.enabled === true;

  const { data: smsCampaignsStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/sms-campaigns/enabled"],
    enabled: !isSuperAdminView,
  });
  const smsCampaignsEnabled = smsCampaignsStatus?.enabled === true;

  const { data: emailCampaignsStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/email-campaigns/enabled"],
    enabled: !isSuperAdminView,
  });
  const emailCampaignsEnabled = emailCampaignsStatus?.enabled === true;

  const { data: whatsappStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/whatsapp/enabled"],
    enabled: !isSuperAdminView,
  });
  const whatsappEnabled = whatsappStatus?.enabled === true;

  const { data: sipForwardingStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/sip-forwarding/enabled"],
    enabled: !isSuperAdminView,
  });
  const sipForwardingEnabled = sipForwardingStatus?.enabled === true;

  const { data: customCallerIdsStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/custom-caller-ids/enabled"],
    enabled: !isSuperAdminView,
  });
  const customCallerIdsEnabled = customCallerIdsStatus?.enabled === true;

  const { data: integrationsStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/integrations/enabled"],
    enabled: !isSuperAdminView,
  });
  const integrationsEnabled = integrationsStatus?.enabled === true;

  const { data: crmAutomationStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/crm-automation/enabled"],
    enabled: !isSuperAdminView,
  });
  const crmAutomationEnabled = crmAutomationStatus?.enabled === true;

  const { data: kbStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/kb/enabled"],
    enabled: !isSuperAdminView,
  });
  const kbEnabled = kbStatus?.enabled === true;

  const { data: notificationsStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/notifications/enabled"],
    enabled: !isSuperAdminView,
  });
  const notificationsEnabled = notificationsStatus?.enabled === true;

  const { data: csatSurveyStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/csat/enabled"],
    enabled: !isSuperAdminView,
  });
  const csatSurveyEnabled = csatSurveyStatus?.enabled === true;

  const { data: voiceAiStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/voice-ai/enabled"],
    enabled: !isSuperAdminView,
  });
  const voiceAiEnabled = voiceAiStatus?.enabled === true;

  const { data: aiAnalysisStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/ai-analysis/enabled"],
    enabled: !isSuperAdminView,
  });
  const aiAnalysisEnabled = aiAnalysisStatus?.enabled === true;

  const branding = usePlatformBranding();
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const sidebarLogoUrl = isDark
    ? (branding.darkLogoUrl || branding.lightLogoUrl || logoPath)
    : (branding.lightLogoUrl || logoPath);

  const superAdminItems = [
    { title: "Dashboard", url: "/", icon: LayoutDashboard },
    { title: "Tenants", url: "/admin/tenants", icon: Shield },
    { title: "Users", url: "/users", icon: UserCog },
    { title: "Customer Support", url: "/customer-support", icon: Headphones },
    { title: "Knowledge Base", url: "/kb-manager", icon: Library },
    { title: "Load Testing", url: "/load-testing", icon: FlaskConical },
    { title: "White Label", url: "/admin/white-label", icon: Palette },
    { title: "Settings", url: "/admin/settings", icon: Settings },
  ];

  const navItems = [
    { title: "Dashboard", url: "/", icon: LayoutDashboard, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Custom Dashboards", url: "/custom-dashboards", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "Call History", url: "/calls", icon: Phone, roles: ["tenant_admin", "supervisor", "agent"] },
    ...(kbEnabled ? [{ title: "Knowledge Base", url: effectiveRole === "agent" ? "/kb-search" : "/kb-manager", icon: Library, roles: ["tenant_admin", "supervisor", "agent"] }] : []),
    { title: "My Profile", url: "/agent-profile", icon: UserCircle, roles: ["tenant_admin", "supervisor", "agent"] },
  ];

  const voicemailSubItems = [
    { title: "Voicemails", url: "/voicemails", icon: Voicemail, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Voicemail Statuses", url: "/voicemail-statuses", icon: Tag, roles: ["super_admin", "tenant_admin"] },
  ];

  const conversationsSubItems = [
    { title: "Active Conversations", url: "/conversations/active", icon: MessageSquare, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Inbox / History", url: "/conversations/inbox", icon: History, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Conversation Dashboard", url: "/conversation-dashboard", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "Conversation Templates", url: "/sms-templates", icon: FileText, roles: ["tenant_admin", "supervisor"] },
    { title: "Live Chat Settings", url: "/livechat-settings", icon: Settings, roles: ["tenant_admin"] },
    { title: "WhatsApp Settings", url: "/whatsapp-settings", icon: Phone, roles: ["tenant_admin"] },
    { title: "Facebook Settings", url: "/facebook-settings", icon: MessageCircle, roles: ["tenant_admin"] },
    { title: "Instagram Settings", url: "/instagram-settings", icon: Camera, roles: ["tenant_admin"] },
    { title: "Team Chat", url: "/team-chat", icon: MessagesSquare, roles: ["tenant_admin", "supervisor", "agent"] },
  ];

  const crmSubItems = [
    { title: "Pipeline", url: "/crm-pipeline", icon: BarChart3, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "My Activities", url: "/crm-activities", icon: CheckSquare, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Activity Tracking", url: "/crm-activity-tracking", icon: Activity, roles: ["tenant_admin", "supervisor"] },
    { title: "Contacts", url: "/contacts", icon: Users, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Contact Lists", url: "/contact-lists", icon: BookOpen, roles: ["tenant_admin", "supervisor"] },
    { title: "DNC Lists", url: "/dnc", icon: ShieldAlert, roles: ["tenant_admin", "supervisor"] },
    { title: "Blocked Numbers", url: "/blocked-numbers", icon: ShieldBan, roles: ["tenant_admin", "supervisor"] },
    ...(crmAutomationEnabled ? [{ title: "Automations", url: "/crm-automations", icon: Workflow, roles: ["tenant_admin", "supervisor"] }] : []),
    { title: "Pipeline Settings", url: "/crm-settings", icon: Settings, roles: ["tenant_admin"] },
  ];

  const ticketingSubItems = [
    { title: "Tickets", url: "/tickets", icon: Ticket, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Ticket History", url: "/ticket-history", icon: History, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Ticket Dashboard", url: "/ticket-dashboard", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "Ticket Templates", url: "/ticket-templates", icon: FileText, roles: ["tenant_admin", "supervisor"] },
    { title: "Ticketing Settings", url: "/ticketing-settings", icon: Settings, roles: ["tenant_admin"] },
  ];

  const powerDialerSubItems = [
    { title: "Dashboard", url: "/campaign-dashboard", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "Campaigns", url: "/campaigns", icon: PhoneOutgoing, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Workflows", url: "/campaign-workflows", icon: Workflow, roles: ["tenant_admin", "supervisor"] },
  ];

  const previewDialerSubItems = [
    { title: "Dashboard", url: "/preview-dialer-dashboard", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "Campaigns", url: "/preview-dialer-campaigns", icon: Eye, roles: ["tenant_admin", "supervisor", "agent"] },
    { title: "Dialer Scripts", url: "/dialer-scripts", icon: ScrollText, roles: ["tenant_admin", "supervisor"] },
  ];

  const screenPopSubItems = [
    { title: "Settings", url: "/screen-pop-settings", icon: Sliders, roles: ["tenant_admin"] },
    { title: "Script Manager", url: "/screen-pop-scripts", icon: ScrollText, roles: ["tenant_admin", "supervisor"] },
    { title: "External Links", url: "/screen-pop-external-links", icon: ExternalLink, roles: ["tenant_admin"] },
  ];

  const reportsSubItems = [
    { title: "Reports", url: "/reports", icon: FileBarChart, roles: ["tenant_admin", "supervisor"] },
    { title: "Conversion Settings", url: "/disposition-conversion-settings", icon: BarChart3, roles: ["tenant_admin"] },
  ];

  const settingsSubItems = [
    { title: "Notifications", url: "/notification-settings", icon: Bell, roles: ["tenant_admin", "supervisor"] },
    { title: "Dispositions", url: "/dispositions", icon: Tag, roles: ["tenant_admin", "supervisor"] },
    { title: "Queues", url: "/queues", icon: ListOrdered, roles: ["tenant_admin", "supervisor"] },
    { title: "Phone Numbers", url: "/phone-numbers", icon: Phone, roles: ["tenant_admin"] },
    { title: "IVR Menus", url: "/ivr", icon: Menu, roles: ["tenant_admin"] },
    { title: "Extensions", url: "/extensions", icon: Hash, roles: ["tenant_admin"] },
    { title: "Recordings", url: "/recordings", icon: Music, roles: ["tenant_admin"] },
    { title: "Business Hours", url: "/business-hours", icon: Clock, roles: ["tenant_admin"] },
    { title: "General Settings", url: "/general-settings", icon: Sliders, roles: ["tenant_admin"] },
    { title: "Agent Statuses", url: "/agent-statuses", icon: UserCheck, roles: ["tenant_admin"] },
    { title: "Billing", url: "/billing", icon: DollarSign, roles: ["tenant_admin"] },
    { title: "Webhooks", url: "/webhook-settings", icon: Webhook, roles: ["tenant_admin"] },
    { title: "API Keys", url: "/api-keys", icon: Key, roles: ["tenant_admin"] },
    { title: "Integrations", url: "/integration-settings", icon: Plug, roles: ["tenant_admin"] },
    { title: "SIP Forwarding", url: "/sip-forwarding", icon: ArrowRightLeft, roles: ["tenant_admin"] },
    { title: "Custom Caller IDs", url: "/custom-caller-ids", icon: PhoneOutgoing, roles: ["super_admin"] },
    { title: "API Docs", url: "/api-docs", icon: BookOpen, roles: ["tenant_admin"] },
    { title: "AI Bot", url: "/ai-bot", icon: Bot, roles: ["tenant_admin"] },
    { title: "Audit Log", url: "/audit-log", icon: ClipboardList, roles: ["tenant_admin"] },
  ];

  const csatSubItems = [
    { title: "CSAT Surveys", url: "/csat-surveys", icon: ListOrdered, roles: ["tenant_admin", "supervisor"] },
    { title: "Survey Results", url: "/csat-survey-results", icon: ClipboardList, roles: ["tenant_admin", "supervisor"] },
    { title: "CSAT Dashboard", url: "/csat-dashboard", icon: BarChart3, roles: ["tenant_admin", "supervisor"] },
    { title: "CSAT Settings", url: "/csat-settings", icon: Settings, roles: ["tenant_admin", "supervisor"] },
  ];

  const adminItems = [
    { title: "Users", url: "/users", icon: UserCog, roles: ["tenant_admin"] },
    { title: "Workforce", url: "/workforce", icon: CalendarDays, roles: ["tenant_admin", "supervisor"] },
    { title: "Permissions", url: "/permissions", icon: ShieldCheck, roles: ["tenant_admin"] },
  ];

  const voiceAiSubItems = voiceAiEnabled ? [
    { title: "Voice AI Agents", url: "/voice-agents", icon: Bot },
    { title: "Voice AI Flows", url: "/voice-flows", icon: GitBranch },
    { title: "Voice AI Analytics", url: "/voice-ai-analytics", icon: BarChart3 },
  ] : [];

  const isAiAnalysisAdmin = effectiveRole === "tenant_admin" || effectiveRole === "super_admin";
  const isAiAnalysisSupervisor = effectiveRole === "supervisor";
  const aiAnalysisSubItems = aiAnalysisEnabled ? [
    // Admin/supervisor: full analytics suite
    ...(isAiAnalysisAdmin || isAiAnalysisSupervisor ? [
      { title: "Dashboard", url: "/ai-analysis", icon: BarChart3 },
    ] : []),
    // All roles: their own analyzed calls
    { title: "Call Records", url: "/ai-analysis/calls", icon: Phone },
    ...(isAiAnalysisAdmin || isAiAnalysisSupervisor ? [
      { title: "Leaderboard", url: "/ai-analysis/leaderboard", icon: Star },
      { title: "Team Analysis", url: "/ai-analysis/team", icon: Users },
      { title: "Historical Trends", url: "/ai-analysis/trends", icon: Activity },
      { title: "One-on-One", url: "/ai-analysis/one-on-one", icon: UserCircle },
    ] : []),
    // Admin only: settings
    ...(isAiAnalysisAdmin
      ? [{ title: "Settings", url: "/ai-analysis/settings", icon: Settings }]
      : []),
  ] : [];

  const filteredNav = navItems.filter(item => hasPermission("General", item.title, effectiveRole));
  const filteredConversations = conversationsSubItems.filter(item => {
    if (!hasPermission("Conversations", item.title, effectiveRole)) return false;
    if (item.url === "/whatsapp-settings" && !whatsappEnabled) return false;
    return true;
  });
  const filteredCrm = crmSubItems.filter(item => hasPermission("CRM", item.title, effectiveRole));
  const filteredTicketing = ticketingSubItems.filter(item => hasPermission("Ticketing", item.title, effectiveRole));
  const filteredPowerDialer = powerDialerSubItems.filter(item => hasPermission("Power Dialer", item.title, effectiveRole));
  const filteredPreviewDialer = previewDialerSubItems.filter(item => hasPermission("Preview Dialer", item.title, effectiveRole));
  const filteredScreenPop = screenPopSubItems.filter(item => hasPermission("Screen Pop", item.title, effectiveRole));
  const filteredCsat = csatSubItems.filter(item => hasPermission("CSAT Surveys", item.title, effectiveRole));
  const filteredReports = reportsSubItems.filter(item => hasPermission("Settings", item.title, effectiveRole));
  const isSuperAdminImpersonating = isImpersonating && user?.role === "super_admin";
  const filteredSettings = settingsSubItems.filter(item => {
    if (item.url === "/custom-caller-ids" && isSuperAdminImpersonating) return true;
    if (!hasPermission("Settings", item.title, effectiveRole)) return false;
    if (item.url === "/sip-forwarding" && !sipForwardingEnabled) return false;
    if (item.url === "/custom-caller-ids" && !customCallerIdsEnabled) return false;
    if (item.url === "/notification-settings" && !notificationsEnabled) return false;
    if (item.url === "/billing" && !user?.canSeeBilling) return false;
    return true;
  });
  const filteredAdmin = adminItems.filter(item => hasPermission("Administration", item.title, effectiveRole));

  const conversationsUrls = [...conversationsSubItems.map(i => i.url), "/conversations/history", "/conversation-history"];
  const isConversationsActive = conversationsUrls.some(url => location === url || location.startsWith(url + "/"));

  const crmUrls = [...crmSubItems.map(i => i.url), "/crm-deals", "/crm-analytics", "/crm-activities", "/crm-activity-tracking", "/crm-automations"];
  const isCrmActive = crmUrls.some(url => location === url || location.startsWith(url + "/") || location.startsWith(url));

  const ticketingUrls = ticketingSubItems.map(i => i.url);
  const isTicketingActive = ticketingUrls.some(url => location === url || location.startsWith(url + "/"));

  const powerDialerUrls = [...powerDialerSubItems.map(i => i.url), "/campaigns", "/campaign-workflows"];
  const isPowerDialerActive = powerDialerUrls.some(url => location === url || location.startsWith(url + "/"));

  const previewDialerUrls = previewDialerSubItems.map(i => i.url);
  const isPreviewDialerActive = previewDialerUrls.some(url => location === url || location.startsWith(url + "/"));

  const screenPopUrls = screenPopSubItems.map(i => i.url);
  const isScreenPopActive = screenPopUrls.some(url => location === url || location.startsWith(url + "/"));

  const csatUrls = csatSubItems.map(i => i.url);
  const isCsatActive = csatUrls.some(url => location === url || location.startsWith(url + "/"));

  const reportsUrls = reportsSubItems.map(i => i.url);
  const isReportsActive = reportsUrls.some(url => location === url || location.startsWith(url + "/"));

  const settingsUrls = settingsSubItems.map(i => i.url);
  const isSettingsActive = settingsUrls.some(url => location === url || location.startsWith(url + "/"));

  const voiceAiUrls = ["/voice-agents", "/voice-flows", "/voice-ai-analytics"];
  const isVoiceAiActive = voiceAiUrls.some(url => location === url || location.startsWith(url + "/"));

  const aiAnalysisUrls = ["/ai-analysis"];
  const isAiAnalysisActive = location === "/ai-analysis" || location.startsWith("/ai-analysis/");

  const emailCampaignsUrls = ["/email-campaigns", "/email-campaigns/sender-signatures", "/email-campaigns/templates"];
  const isEmailCampaignsActive = emailCampaignsUrls.some(url => location === url || location.startsWith(url + "/"));

  const voicemailUrls = voicemailSubItems.map(i => i.url);
  const isVoicemailsActive = voicemailUrls.some(url => location === url || location.startsWith(url + "/"));
  const filteredVoicemailSub = voicemailSubItems.filter(item => item.roles.includes(effectiveRole));

  const initialSection =
    isConversationsActive ? "conversations" :
    isCrmActive ? "crm" :
    isTicketingActive ? "ticketing" :
    isPowerDialerActive ? "powerDialer" :
    isPreviewDialerActive ? "previewDialer" :
    isScreenPopActive ? "screenPop" :
    isCsatActive ? "csat" :
    isReportsActive ? "reports" :
    isVoiceAiActive ? "voiceAi" :
    isAiAnalysisActive ? "aiAnalysis" :
    isEmailCampaignsActive ? "emailCampaigns" :
    isVoicemailsActive ? "voicemails" :
    isSettingsActive ? "settings" : null;

  const [openSection, setOpenSection] = useState<string | null>(initialSection);

  useEffect(() => {
    if (isConversationsActive) setOpenSection("conversations");
    else if (isCrmActive) setOpenSection("crm");
    else if (isTicketingActive) setOpenSection("ticketing");
    else if (isPowerDialerActive) setOpenSection("powerDialer");
    else if (isPreviewDialerActive) setOpenSection("previewDialer");
    else if (isScreenPopActive) setOpenSection("screenPop");
    else if (isCsatActive) setOpenSection("csat");
    else if (isReportsActive) setOpenSection("reports");
    else if (isVoiceAiActive) setOpenSection("voiceAi");
    else if (isAiAnalysisActive) setOpenSection("aiAnalysis");
    else if (isEmailCampaignsActive) setOpenSection("emailCampaigns");
    else if (isVoicemailsActive) setOpenSection("voicemails");
    else if (isSettingsActive) setOpenSection("settings");
    else setOpenSection(null);
  }, [location]);

  const toggleSection = (section: string) =>
    setOpenSection(prev => (prev === section ? null : section));

  const conversationsOpen = openSection === "conversations";
  const crmOpen = openSection === "crm";
  const ticketingOpen = openSection === "ticketing";
  const powerDialerOpen = openSection === "powerDialer";
  const previewDialerOpen = openSection === "previewDialer";
  const screenPopOpen = openSection === "screenPop";
  const csatOpen = openSection === "csat";
  const reportsOpen = openSection === "reports";
  const voiceAiOpen = openSection === "voiceAi";
  const emailCampaignsOpen = openSection === "emailCampaigns";
  const voicemailsOpen = openSection === "voicemails";
  const settingsOpen = openSection === "settings";

  const roleLabel: Record<string, string> = {
    super_admin: "Super Admin",
    tenant_admin: "Admin",
    supervisor: "Supervisor",
    agent: "Agent",
  };

  return (
    <Sidebar onClickCapture={handleSidebarClick}>
      <SidebarHeader className="p-4">
        <Link href="/" className="flex items-center gap-2.5">
          <img src={sidebarLogoUrl} alt={branding.appName} className="h-8 w-auto max-w-[120px] object-contain shrink-0" />
          {(!branding.hideSidebarAppName || !branding.hideSidebarTagline) && (
            <div className="min-w-0">
              {!branding.hideSidebarAppName && (
                <h1 className="text-sm font-bold tracking-tight truncate" data-testid="text-app-name">{branding.appName}</h1>
              )}
              {!branding.hideSidebarTagline && (
                <p className="text-[10px] text-muted-foreground leading-none">
                  {isImpersonating ? impersonatingTenantName : (user?.tenantName || branding.loginTagline)}
                </p>
              )}
            </div>
          )}
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {isSuperAdminView ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {superAdminItems.map((item) => {
                  const isActive = location === item.url || (item.url !== "/" && location.startsWith(item.url));
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                          <item.icon className="w-4 h-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Navigation</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredNav.map((item) => {
                    const isActive = location === item.url || (item.url !== "/" && location.startsWith(item.url));
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild isActive={isActive}>
                          <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                            <item.icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  {filteredVoicemailSub.length > 0 && (
                    <Collapsible open={voicemailsOpen} onOpenChange={() => toggleSection("voicemails")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isVoicemailsActive} data-testid="link-nav-voicemails">
                            <Voicemail className="w-4 h-4" />
                            <span>Voicemails</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredVoicemailSub.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {filteredConversations.length > 0 && (
                    <Collapsible open={conversationsOpen} onOpenChange={() => toggleSection("conversations")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isConversationsActive} data-testid="link-nav-conversations">
                            <MessageSquare className="w-4 h-4" />
                            <span>Conversations</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredConversations.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/") ||
                                (item.url === "/conversations/inbox" && location === "/conversations/history");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {filteredCrm.length > 0 && (
                    <Collapsible open={crmOpen} onOpenChange={() => toggleSection("crm")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isCrmActive} data-testid="link-nav-crm">
                            <BookUser className="w-4 h-4" />
                            <span>CRM</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredCrm.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {ticketingEnabled && filteredTicketing.length > 0 && (
                    <Collapsible open={ticketingOpen} onOpenChange={() => toggleSection("ticketing")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isTicketingActive} data-testid="link-nav-ticketing">
                            <Ticket className="w-4 h-4" />
                            <span>Ticketing</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredTicketing.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {csatSurveyEnabled && filteredCsat.length > 0 && (
                    <Collapsible open={csatOpen} onOpenChange={() => toggleSection("csat")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isCsatActive} data-testid="link-nav-csat-survey">
                            <SmilePlus className="w-4 h-4" />
                            <span>CSAT Survey</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredCsat.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {filteredReports.length > 0 && (
                    <Collapsible open={reportsOpen} onOpenChange={() => toggleSection("reports")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isReportsActive} data-testid="link-nav-reports">
                            <FileBarChart className="w-4 h-4" />
                            <span>Reports</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredReports.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {voiceAiEnabled && voiceAiSubItems.length > 0 && (
                    <Collapsible open={voiceAiOpen} onOpenChange={() => toggleSection("voiceAi")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isVoiceAiActive} data-testid="link-nav-voice-ai">
                            <BrainCircuit className="w-4 h-4" />
                            <span>AI Voice Agent</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {voiceAiSubItems.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {aiAnalysisEnabled && aiAnalysisSubItems.length > 0 && (
                    <Collapsible open={openSection === "aiAnalysis"} onOpenChange={() => toggleSection("aiAnalysis")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isAiAnalysisActive} data-testid="link-nav-ai-analysis">
                            <Sparkles className="w-4 h-4" />
                            <span>AI Analysis</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {aiAnalysisSubItems.map((item) => {
                              const isActive = item.url === "/ai-analysis" ? location === "/ai-analysis" : location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-ai-analysis-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {powerDialerEnabled && filteredPowerDialer.length > 0 && (
                    <Collapsible open={powerDialerOpen} onOpenChange={() => toggleSection("powerDialer")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isPowerDialerActive} data-testid="link-nav-power-dialer">
                            <PhoneOutgoing className="w-4 h-4" />
                            <span>Power Dialer</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredPowerDialer.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {previewDialerEnabled && filteredPreviewDialer.length > 0 && (
                    <Collapsible open={previewDialerOpen} onOpenChange={() => toggleSection("previewDialer")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isPreviewDialerActive} data-testid="link-nav-preview-dialer">
                            <Eye className="w-4 h-4" />
                            <span>Preview Dialer</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredPreviewDialer.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-preview-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {smsCampaignsEnabled && hasPermission("SMS Campaigns", "SMS Campaigns", effectiveRole) && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={location === "/sms-campaigns"} data-testid="link-nav-sms-campaigns">
                        <Link href="/sms-campaigns">
                          <Send className="w-4 h-4" />
                          <span>SMS Campaigns</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {emailCampaignsEnabled && effectiveRole !== "agent" && (
                    <Collapsible open={emailCampaignsOpen} onOpenChange={() => toggleSection("emailCampaigns")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isEmailCampaignsActive} data-testid="link-nav-email-campaigns">
                            <Mail className="w-4 h-4" />
                            <span>Email Campaigns</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton asChild isActive={location === "/email-campaigns"}>
                                <Link href="/email-campaigns" data-testid="link-nav-email-campaigns-list">
                                  <Mail className="w-4 h-4" />
                                  <span>Campaigns</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton asChild isActive={location === "/email-campaigns/sender-signatures"}>
                                <Link href="/email-campaigns/sender-signatures" data-testid="link-nav-email-sender-signatures">
                                  <AtSign className="w-4 h-4" />
                                  <span>Sender Signatures</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton asChild isActive={location === "/email-campaigns/templates"}>
                                <Link href="/email-campaigns/templates" data-testid="link-nav-email-templates">
                                  <FileText className="w-4 h-4" />
                                  <span>Email Templates</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {filteredScreenPop.length > 0 && (
                    <Collapsible open={screenPopOpen} onOpenChange={() => toggleSection("screenPop")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isScreenPopActive} data-testid="link-nav-screen-pop">
                            <Monitor className="w-4 h-4" />
                            <span>Screen Pop</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredScreenPop.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-screenpop-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                  {filteredSettings.length > 0 && (
                    <Collapsible open={settingsOpen} onOpenChange={() => toggleSection("settings")} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={isSettingsActive} data-testid="link-nav-settings">
                            <Settings className="w-4 h-4" />
                            <span>Settings</span>
                            <ChevronDown className="ml-auto w-4 h-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredSettings.map((item) => {
                              const isActive = location === item.url || location.startsWith(item.url + "/");
                              return (
                                <SidebarMenuSubItem key={item.title}>
                                  <SidebarMenuSubButton asChild isActive={isActive}>
                                    <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                                      <item.icon className="w-4 h-4" />
                                      <span>{item.title}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {filteredAdmin.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Administration</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredAdmin.map((item) => {
                      const isActive = location === item.url || location.startsWith(item.url);
                      return (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild isActive={isActive}>
                            <Link href={item.url} data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                              <item.icon className="w-4 h-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="p-4 space-y-3">
        {connectedEndpointId && (
          <div data-testid="agent-status-selector">
            <div className="flex items-center gap-1.5 mb-1.5">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Agent Status</p>
              <span
                className={`w-2 h-2 rounded-full ${isRegistered ? "bg-green-500" : "bg-muted-foreground"}`}
                title={isRegistered ? "SIP Connected" : "Not Connected"}
                data-testid="status-sip-indicator"
              />
            </div>
            {currentStatusInfo.selectable === false ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 rounded-md border px-3 py-2" data-testid="button-agent-status">
                  <span className={`w-2 h-2 rounded-full ${currentStatusInfo.color}`} />
                  <span className="text-sm">{currentStatusInfo.label}</span>
                </div>
                {isStuckOnCall && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => forceReadyMutation.mutate()}
                    disabled={forceReadyMutation.isPending}
                    data-testid="button-force-ready"
                  >
                    {forceReadyMutation.isPending ? "Resetting..." : "Force Ready (No Active Call)"}
                  </Button>
                )}
              </div>
            ) : (
              <Select
                value={currentStatus}
                onValueChange={(val) => statusMutation.mutate(val)}
              >
                <SelectTrigger className="w-full" data-testid="button-agent-status">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${currentStatusInfo.color}`} />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {allStatusOptions.filter(opt => opt.selectable !== false).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`menu-status-${opt.value}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${opt.color}`} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
